import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { CreatePolicyDto } from '../../dtos/create-policy.dto';
import { UpdatePolicyDto } from '../../dtos/update-policy.dto';
import { createPolicySchema } from '../schemas/create-policy.schema';
import { updatePolicySchema } from '../schemas/update-policy.schema';

/**
 * Validates policy record data for creation or update.
 *
 * @param record - The record to validate.
 * @param abortEarly - Whether to abort validation on the first error.
 * @returns Validation outcome with optional error messages.
 */
export const canCreateOrUpdate = (
  record: CreatePolicyDto | UpdatePolicyDto,
  abortEarly: boolean = false,
): ValidationFunctionResult => {
  if (!record) {
    throw new Error('record is required.');
  }

  let validationResult: ValidationResult | undefined;

  const maybeId = 'id' in record ? record.id : undefined;
  const hasPersistedId = maybeId != null;

  if (hasPersistedId) {
    validationResult = updatePolicySchema().validate(record, {
      abortEarly,
    });
  } else {
    const { id: _discardedPolicyId, ...createPayload } =
      record as CreatePolicyDto & {
        id?: number | null;
      };

    void _discardedPolicyId;

    validationResult = createPolicySchema().validate(createPayload, {
      abortEarly,
    });
  }

  if (validationResult?.error) {
    return {
      isValid: false,
      errors: validationResult.error.details?.map((detail) => detail.message),
    };
  }

  return { isValid: true };
};
